import { User, Menu, X as XIcon } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Logo, LogoCompact } from './Logo'
import { NotificationBell } from './NotificationBell'
import ContactForm from './ContactForm'
import AppMenuDropdown from './AppMenuDropdown'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { Tooltip } from './Tooltip'
import { isNativeApp, isIOS } from '../lib/platform'
import { useStore } from '../store/useStore'

export const APP_VERSION = '1.3.576'

interface HeaderProps {
  onOpenSettings?: () => void
  /** モバイルではヘッダーを非表示にする（機能は下部バーに集約。ダッシュボードで使用） */
  hideOnMobile?: boolean
}

export default function Header({ onOpenSettings, hideOnMobile = false }: HeaderProps = {}) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [nickname, setNickname] = useState<string>('')
  const menuRef = useRef<HTMLDivElement>(null)
  const { ganttFullscreenMode } = useStore()

  // ガント最大化モード時はヘッダーを広げる
  const isExpanded = ganttFullscreenMode !== 'none'

  useEffect(() => {
    if (user) {
      loadNickname()
    }

    // Listen for nickname changes from UserSettings
    const handleNicknameChanged = (event: CustomEvent) => {
      setNickname(event.detail.nickname)
    }

    window.addEventListener('nicknameChanged', handleNicknameChanged as EventListener)

    return () => {
      window.removeEventListener('nicknameChanged', handleNicknameChanged as EventListener)
    }
  }, [user])

  const loadNickname = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('nickname')
        .eq('id', user?.id)
        .single()

      if (error) throw error

      if (data) {
        setNickname(data.nickname)
      }
    } catch (error) {
      console.error('Error loading nickname:', error)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  // テスト環境判定
  const isTestEnvironment = window.location.hostname === 'test.tastack.net'

  // ネイティブアプリかどうか
  const isNative = isNativeApp()

  return (
    <>
    {/* ステータスバー領域 + ヘッダー（モバイル: fixed、PC: static） */}
    <div className="fixed top-0 left-0 right-0 z-40 lg:relative">
      {/* ネイティブアプリ: ステータスバー領域を紫色で塗りつぶす */}
      {/* iOS: safe-area-inset-topでノッチ/ダイナミックアイランド対応 */}
      {/* Android: overlaysWebView: trueのためステータスバー高さ(24px)分のスペースが必要 */}
      {isNative && (
        <div
          className="bg-indigo-600"
          style={{ height: isIOS() ? 'env(safe-area-inset-top, 0px)' : '24px' }}
        />
      )}
      {/* テスト環境バナー */}
      {isTestEnvironment && (
        <div className="bg-yellow-500 text-white text-center py-1 px-4 text-xs sm:text-sm font-semibold">
          ⚠️ テスト環境 - TEST ENVIRONMENT - このサイトは検証用です
        </div>
      )}
    <header className={`bg-white shadow-sm border-b ${hideOnMobile ? 'hidden lg:block' : ''}`}>
      <div className={`${isExpanded ? '' : 'container mx-auto'} px-2 sm:px-4 py-1 sm:py-2`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center min-w-0">
            <div className="hidden sm:block">
              <Logo />
            </div>
            <div className="sm:hidden">
              <LogoCompact />
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {user && (
              <>
                <div className="flex items-center text-xs text-gray-600 max-w-[120px] sm:max-w-[150px]">
                  <User className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{nickname || user.email}</span>
                </div>

                {/* 通知ベル */}
                <Tooltip content={t('header.notifications')}>
                  <NotificationBell />
                </Tooltip>

                {/* メニューボタン */}
                <div className="relative" ref={menuRef} data-tour="user-menu">
                  <Tooltip content={t('header.menu')}>
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 rounded transition-colors"
                    >
                      {isMenuOpen ? <XIcon className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                  </Tooltip>

                  {/* ドロップダウンメニュー */}
                  {isMenuOpen && (
                    <AppMenuDropdown
                      direction="down"
                      onClose={() => setIsMenuOpen(false)}
                      onOpenSettings={onOpenSettings}
                      onOpenContactForm={() => setShowContactForm(true)}
                    />
                  )}
                </div>
              </>
            )}
            <span className="text-[10px] text-gray-400">v{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </header>
    </div>

    {/* お問い合わせモーダル */}
    {showContactForm && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowContactForm(false)
          }
        }}
      >
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative p-6">
          <button
            onClick={() => setShowContactForm(false)}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
            aria-label="閉じる"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pr-8">{t('contact.newContact')}</h2>
          <ContactForm
            onClose={() => setShowContactForm(false)}
            userName={user?.user_metadata?.nickname || user?.user_metadata?.full_name || ''}
            userEmail={user?.email || ''}
          />
        </div>
      </div>
    )}
  </>
  )
}